import useNavigationStore from '@src/stores/navigationStore';
import useRegistrationStore from '@src/stores/registrationStore';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import BackButton from '@src/components/_shared/BackButton';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import { Form, Formik } from 'formik';
import Input from '@src/components/_shared/UI/Input';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import { formatIban, handleApiError, unformatIban } from '@src/utilities/functions';
import { ibanSchema } from '@src/utilities/schemas';
import { useMutation } from '@tanstack/react-query';
import { accountOpen, addNewIban } from '@src/api/Registration';
import { useState } from 'react';
import Loader from '@src/components/_shared/UI/Loader';
import { IbanType } from '@src/utilities/types';

export default function Iban() {
    const { setSection, setBlock } = useNavigationStore();
    const { registration, updateIbanField, updateAccountField } = useRegistrationStore();
    const [displayConfirmation, setDisplayConfirmation] = useState<boolean>(false || !!registration.company.ibans[0]?.iban);
    const [apiError, setApiError] = useState<string | null>(null);

    const _onGoBack = () => {
        setSection(5)
        setBlock('AccountInformations')
    };

    const { mutate: accountMutation, isPending: isAccountPending } = useMutation({
        mutationFn: () => accountOpen({demand_id: registration.demand_id, accounts: registration.accounts, partner_informations: registration.partner_informations, person_id: registration.person_id}), 
        onSuccess: (data: any) => {
            const updates = {
                id: data.accounts[0].id,
                demand_id: data.accounts[0].demand_id,
                is_transfer: data.accounts[0].is_transfer,
                is_peaj: data.accounts[0].is_peaj,
                type: data.accounts[0].type,
            };
            Object.entries(updates).forEach(([key, value]) => {
                updateAccountField(0, key, value);
            });
            setSection(6);
            setBlock('Documents');
        },
        onError: (error: any) => handleApiError(error)
    });

    const { mutate, isPending } = useMutation({
        mutationFn: ({ iban }: {iban: string}) => addNewIban({iban, demand_id: registration.demand_id}),
        onSuccess: ({ data }: {data: IbanType}) => {
            updateIbanField(0, 'iban', data.iban);
            updateIbanField(0, 'bic', data.bic);
            updateIbanField(0, 'bank_name', data.bank_name);
            setDisplayConfirmation(true);
        },
        onError: (error: any) => {
            if (error?.response?.data?.message === 'structure not valid') {
                return setApiError('Le format de l\'IBAN est incorrect');
            } else {
                handleApiError(error)
            }
        }
    })

    const _onModifyIban = () => {
        updateIbanField(0, 'iban', '');
        updateIbanField(0, 'bic', '');
        updateIbanField(0, 'bank_name', '');
        setDisplayConfirmation(false);
    }

    if (displayConfirmation) {
        return (
            <Container>
                <BackButton onClick={_onGoBack}/>
                <BlockTitle title="Propriétés et données bancaires" />
                <Subtitle content="Merci de renseigner les coordonnées bancaires de la sociéte" />
                <Input
                    title="IBAN"
                    value={registration.company.ibans[0].iban}
                    type="text"
                    width="100%"
                    disabled
                    showCheckIcon
                />
                <Input
                    title="BIC"
                    value={registration.company.ibans[0].bic}
                    id="bic"
                    name="bic"
                    type="text"
                    width="100%"
                    disabled
                    showCheckIcon
                />
                <Input
                    title="Nom de la banque"
                    value={registration.company.ibans[0].bank_name}
                    id="bank_name"
                    name="bank_name"
                    type="text"
                    width="100%"
                    disabled
                    showCheckIcon
                />
                {isAccountPending ?
                    <Loader type="SPINNER" />
                    :
                    <div>
                        <ValidateButton title="Continuer" disabled={false} onClick={accountMutation} />
                        <ValidateButton inverted title="Modifier" disabled={false} onClick={_onModifyIban} />
                    </div>
                }
            </Container>
        )
    }

    return (
        <Container>
            <BackButton onClick={_onGoBack}/>
            <BlockTitle title="Propriétés et données bancaires" />
            <Subtitle content="Merci de renseigner les coordonnées bancaires de la société" />
            <Formik
                initialValues={{
                    iban: registration.company.ibans[0]?.iban || ''
                }}
                onSubmit={(values) => {
                    const formattedIban = unformatIban(values.iban);
                    updateIbanField(0, 'iban', formattedIban);
                    mutate({iban: formattedIban});
                }}
                validationSchema={ibanSchema}
            >
                {({ handleChange, handleSubmit, values, errors, touched, handleBlur }) => {
                    return (
                        <Form id="ibanForm" autoComplete="off" className="registerForm">
                            <Input
                                title="IBAN"
                                value={values.iban}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setApiError(null);
                                    const inputValue = e.target.value.toUpperCase();
                                    const formattedValue = formatIban(inputValue);
                                    handleChange({
                                        target: {
                                            name: 'iban',
                                            value: formattedValue,
                                        },
                                    });
                                }}
                                placeholder="Ex : FR76 3000 6000 0112 3456 7890 189"
                                id="iban"
                                name="iban"
                                type="text"
                                width="100%"
                                clearInput={() => handleChange('iban')('')}
                                hasError={((touched.iban && errors.iban) || !!apiError) as boolean}
                                errorMessage={apiError || errors.iban}
                                onBlur={handleBlur('iban')}
                            />
                            <ValidateButton title="Vérifier" disabled={!values.iban} onClick={handleSubmit} onLoading={isPending || isAccountPending} />
                        </Form>
                    )
                }}
            </Formik>
        </Container>
    );
}
